import React, { useState } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import upsService, { UPSShipmentRequest, UPSShipmentResponse } from '@/services/upsService';
import styles from './CreateLabelModal.module.scss';

interface CreateLabelModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: (response: UPSShipmentResponse) => void;
  orderData?: {
    orderNumber: string;
    name: string;
    orderDate: string;
  };
}

const CreateLabelModal: React.FC<CreateLabelModalProps> = ({
  show,
  onHide,
  onSuccess,
  orderData,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    // Shipper (From) - Your warehouse address
    shipperName: 'KrossKast LLC',
    shipperAttention: 'Shipping Department',
    shipperPhone: '4045551234',
    shipperAddress1: '123 Warehouse St',
    shipperAddress2: '',
    shipperCity: 'Atlanta',
    shipperState: 'GA',
    shipperZip: '30303',
    shipperCountry: 'US',
    
    // Recipient (To)
    recipientName: orderData?.name || '',
    recipientAttention: '',
    recipientPhone: '',
    recipientAddress1: '',
    recipientAddress2: '',
    recipientCity: '',
    recipientState: '',
    recipientZip: '',
    recipientCountry: 'US',
    
    // Package details
    weight: '5',
    weightUnit: 'LBS' as 'LBS' | 'KGS',
    length: '',
    width: '',
    height: '',
    dimensionUnit: 'IN' as 'IN' | 'CM',
    
    // Service
    serviceCode: '03', // UPS Ground
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const shipperAddress = [formData.shipperAddress1];
      if (formData.shipperAddress2) {
        shipperAddress.push(formData.shipperAddress2);
      }

      const recipientAddress = [formData.recipientAddress1];
      if (formData.recipientAddress2) {
        recipientAddress.push(formData.recipientAddress2);
      }

      const shipmentRequest: UPSShipmentRequest = {
        shipper: {
          name: formData.shipperName,
          attentionName: formData.shipperAttention,
          phone: formData.shipperPhone,
          address: {
            addressLine: shipperAddress,
            city: formData.shipperCity,
            stateProvinceCode: formData.shipperState,
            postalCode: formData.shipperZip,
            countryCode: formData.shipperCountry,
          },
        },
        shipTo: {
          name: formData.recipientName,
          attentionName: formData.recipientAttention || formData.recipientName,
          phone: formData.recipientPhone,
          address: {
            addressLine: recipientAddress,
            city: formData.recipientCity,
            stateProvinceCode: formData.recipientState,
            postalCode: formData.recipientZip,
            countryCode: formData.recipientCountry,
          },
        },
        package: {
          weight: formData.weight,
          weightUnit: formData.weightUnit,
          dimensions: formData.length && formData.width && formData.height ? {
            length: formData.length,
            width: formData.width,
            height: formData.height,
            unit: formData.dimensionUnit,
          } : undefined,
        },
        service: {
          code: formData.serviceCode,
          description: getServiceDescription(formData.serviceCode),
        },
      };

      const response = await upsService.createShipment(shipmentRequest);
      onSuccess(response);
      onHide();
    } catch (err: any) {
      console.error('Failed to create label:', err);
      setError(err.message || 'Failed to create shipping label. Please check your information and try again.');
    } finally {
      setLoading(false);
    }
  };

  const getServiceDescription = (code: string): string => {
    const services: Record<string, string> = {
      '01': 'UPS Next Day Air',
      '02': 'UPS Second Day Air',
      '03': 'UPS Ground',
      '12': 'UPS 3 Day Select',
      '13': 'UPS Next Day Air Saver',
      '14': 'UPS Next Day Air Early',
      '59': 'UPS Second Day Air A.M.',
    };
    return services[code] || 'UPS Service';
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton className={styles.modalHeader}>
        <Modal.Title>Create Shipping Label</Modal.Title>
      </Modal.Header>
      <Modal.Body className={styles.modalBody}>
        {orderData && (
          <Alert variant="info" className={styles.orderInfo}>
            <strong>Order:</strong> {orderData.orderNumber} | <strong>Customer:</strong> {orderData.name}
          </Alert>
        )}

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          <Row>
            {/* Shipper Section */}
            <Col md={6}>
              <h5 className={styles.sectionTitle}>From (Shipper)</h5>
              <Form.Group className="mb-3">
                <Form.Label>Company Name *</Form.Label>
                <Form.Control
                  name="shipperName"
                  value={formData.shipperName}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Attention Name</Form.Label>
                <Form.Control
                  name="shipperAttention"
                  value={formData.shipperAttention}
                  onChange={handleChange}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Phone *</Form.Label>
                <Form.Control
                  name="shipperPhone"
                  value={formData.shipperPhone}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Address Line 1 *</Form.Label>
                <Form.Control
                  name="shipperAddress1"
                  value={formData.shipperAddress1}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Address Line 2</Form.Label>
                <Form.Control
                  name="shipperAddress2"
                  value={formData.shipperAddress2}
                  onChange={handleChange}
                />
              </Form.Group>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>City *</Form.Label>
                    <Form.Control
                      name="shipperCity"
                      value={formData.shipperCity}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>State *</Form.Label>
                    <Form.Control
                      name="shipperState"
                      value={formData.shipperState}
                      onChange={handleChange}
                      maxLength={2}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>ZIP *</Form.Label>
                    <Form.Control
                      name="shipperZip"
                      value={formData.shipperZip}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Col>

            {/* Recipient Section */}
            <Col md={6}>
              <h5 className={styles.sectionTitle}>To (Recipient)</h5>
              <Form.Group className="mb-3">
                <Form.Label>Name *</Form.Label>
                <Form.Control
                  name="recipientName"
                  value={formData.recipientName}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Attention Name</Form.Label>
                <Form.Control
                  name="recipientAttention"
                  value={formData.recipientAttention}
                  onChange={handleChange}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Phone *</Form.Label>
                <Form.Control
                  name="recipientPhone"
                  value={formData.recipientPhone}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Address Line 1 *</Form.Label>
                <Form.Control
                  name="recipientAddress1"
                  value={formData.recipientAddress1}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Address Line 2</Form.Label>
                <Form.Control
                  name="recipientAddress2"
                  value={formData.recipientAddress2}
                  onChange={handleChange}
                />
              </Form.Group>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>City *</Form.Label>
                    <Form.Control
                      name="recipientCity"
                      value={formData.recipientCity}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>State *</Form.Label>
                    <Form.Control
                      name="recipientState"
                      value={formData.recipientState}
                      onChange={handleChange}
                      maxLength={2}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>ZIP *</Form.Label>
                    <Form.Control
                      name="recipientZip"
                      value={formData.recipientZip}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Col>
          </Row>

          <hr />

          {/* Package and Service Details */}
          <Row>
            <Col md={6}>
              <h5 className={styles.sectionTitle}>Package Details</h5>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Weight *</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.1"
                      name="weight"
                      value={formData.weight}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Unit *</Form.Label>
                    <Form.Select
                      name="weightUnit"
                      value={formData.weightUnit}
                      onChange={handleChange}
                    >
                      <option value="LBS">Pounds (LBS)</option>
                      <option value="KGS">Kilograms (KGS)</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <p className={styles.helperText}>Dimensions (optional)</p>
              <Row>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Length</Form.Label>
                    <Form.Control
                      type="number"
                      name="length"
                      value={formData.length}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Width</Form.Label>
                    <Form.Control
                      type="number"
                      name="width"
                      value={formData.width}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Height</Form.Label>
                    <Form.Control
                      type="number"
                      name="height"
                      value={formData.height}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Unit</Form.Label>
                    <Form.Select
                      name="dimensionUnit"
                      value={formData.dimensionUnit}
                      onChange={handleChange}
                    >
                      <option value="IN">Inches</option>
                      <option value="CM">Centimeters</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </Col>

            <Col md={6}>
              <h5 className={styles.sectionTitle}>Shipping Service</h5>
              <Form.Group className="mb-3">
                <Form.Label>Service Type *</Form.Label>
                <Form.Select
                  name="serviceCode"
                  value={formData.serviceCode}
                  onChange={handleChange}
                  required
                >
                  <option value="03">UPS Ground</option>
                  <option value="02">UPS Second Day Air</option>
                  <option value="01">UPS Next Day Air</option>
                  <option value="13">UPS Next Day Air Saver</option>
                  <option value="12">UPS 3 Day Select</option>
                  <option value="59">UPS Second Day Air A.M.</option>
                  <option value="14">UPS Next Day Air Early</option>
                </Form.Select>
              </Form.Group>
              {process.env.REACT_APP_UPS_ENVIRONMENT === 'sandbox' && (
                <Alert variant="warning" className={styles.sandboxAlert}>
                  <strong>Sandbox Mode:</strong> This will create a test label. No actual shipment will be created.
                </Alert>
              )}
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer className={styles.modalFooter}>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={loading}
          className={styles.createButton}
        >
          {loading ? 'Creating Label...' : 'Create Label'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateLabelModal;

